import { AmChartsModule } from '@amcharts/amcharts3-angular';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import de from '@angular/common/locales/de';
import extraDe from '@angular/common/locales/extra/de';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideEnvironmentInitializer
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideCinetixxUi } from '@cinetixx/cinetixx-ui';
import * as Sentry from '@sentry/angular';
import { DragulaModule } from 'ng2-dragula';
import { routes } from './app.routes';
import { AuthService } from './auth/core/services/AuthService';
import { HttpRequestInterceptor } from './core/interceptors/http-request.interceptor';
import { HttpTokenInterceptor } from './core/interceptors/http-token.interceptor';
import { IdentityService } from './core/services/IdentityService';
import { StaticDependenciesService } from './core/services/StaticDependenciesService';
import { TableConfigDatabaseStrategy } from './core/strategies/TableConfigDatabaseStrategy';
import { TableStateDatabaseStrategy } from './core/strategies/TableStateDatabaseStrategy';
import { environment } from '../environments/environment';
import { TableExportStrategy } from './exports/core/strategies/TableExportStrategy';
import { languagesSnippets$ } from './language/core/guards';

const localeLoaders: Record<string, () => Promise<void>> = {
  de: async () => {
    const [locale, extra] = await Promise.all([
      import('@angular/common/locales/de'),
      import('@angular/common/locales/extra/de'),
    ]);
    registerLocaleData(locale.default, 'main', extra.default);
  },
  en: async () => {
    const [locale, extra] = await Promise.all([
      import('@angular/common/locales/en'),
      import('@angular/common/locales/extra/en'),
    ]);
    registerLocaleData(locale.default, 'main', extra.default);
  },
};

function setLocales(): void {
  const lang = location.pathname.split('/')[1]?.split('-')[0]?.toLowerCase() ?? 'de';
  const load = localeLoaders[lang] ?? (() => Promise.resolve(registerLocaleData(de, 'main', extraDe)));

  load().catch(() => registerLocaleData(de, 'main', extraDe));
}

setLocales();

function buildCinetixxUiConfig(lang: string) {
  return {
    transFunction: String().trans,
    app: environment.app,
    defaultTimeFormat: environment.defaultTimeFormat,
    defaultDateFormat: environment.defaultDateFormat,
    lang,
    production: environment.production,
    cloudFrontUrl: environment.cloudFrontUrl,
    defaultTableStateStrategy: TableStateDatabaseStrategy,
    defaultTableConfigStrategy: TableConfigDatabaseStrategy<any>,
    defaultTableExportStrategy: TableExportStrategy,
    fallbackLanguage: environment.fallbackLanguage,
    brand: environment.brand
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling({
      scrollPositionRestoration: 'enabled'
    })),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.isSW
    }),
    importProvidersFrom(AmChartsModule, DragulaModule.forRoot()),
    {
      provide: LOCALE_ID,
      useValue: 'main'
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR'
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => {
      const authService = inject(AuthService);
      const identityService = inject(IdentityService);

      if (!identityService.identity) {
        return Promise.resolve();
      }

      return authService.getStatus();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpTokenInterceptor,
      multi: true
    },
    StaticDependenciesService,
    provideCinetixxUi(buildCinetixxUiConfig(environment.fallbackLanguage)),
    provideEnvironmentInitializer(() => {
      inject(StaticDependenciesService);

      languagesSnippets$.subscribe(() => {
        provideCinetixxUi(buildCinetixxUiConfig(StaticDependenciesService.languageStoreService.language.token));
      });
    }),
  ]
};
